import { MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../api/client";
import type { SupportContact, SupportContactsResponse } from "../api/types";

/**
 * Die zwei Ansprechpersonen des Supports. Wer die beiden sind, entscheidet der
 * Server anhand der Id des Kontos und antwortet fuer dasselbe Konto immer
 * gleich — man landet nicht jedes Mal bei jemand anderem.
 *
 * Eine leere Liste heisst: niemand eingerichtet. Wer das Menue anbietet, blendet
 * den Eintrag dann aus, statt ein leeres Fenster zu oeffnen.
 */
export function useSupportContacts(): SupportContact[] {
  const support = useQuery({
    queryKey: ["main", "support"],
    queryFn: () => apiFetch<SupportContactsResponse>("/main/support"),
    // Die Liste haengt am Konto und aendert sich innerhalb einer Sitzung nicht.
    staleTime: Infinity,
  });

  return support.data?.data ?? [];
}

/**
 * Der Inhalt: Name, Telefonnummer als waehlbarer Link, Adresse als Mail-Link —
 * dasselbe, was das Kopfzeilen-Menue der alten Oberflaeche zeigte, nur ohne die
 * Bilder.
 */
export function SupportContacts({ contacts }: { contacts: SupportContact[] }) {
  const { t } = useTranslation();

  return (
    <Space orientation="vertical" size={12} style={{ width: "100%" }}>
      <Typography.Text type="secondary">{t("navigation.support_hint")}</Typography.Text>
      {contacts.map((contact) => (
        <Space key={contact.email} orientation="vertical" size={0}>
          <Typography.Text strong>{contact.name}</Typography.Text>
          <Space size={6}>
            <PhoneOutlined />
            <Typography.Link href={`tel:${contact.phone.replace(/\s+/g, "")}`}>
              {contact.phone}
            </Typography.Link>
          </Space>
          <Space size={6}>
            <MailOutlined />
            <Typography.Link href={`mailto:${contact.email}`}>{contact.email}</Typography.Link>
          </Space>
        </Space>
      ))}
    </Space>
  );
}
